import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";

import Stack from "@mui/material/Stack";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import { SIZES } from "app/routes/constant";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import {
  createSizeChart,
  deleteSizeChart,
  getSizeChartList,
  updateSizeChart,
} from "app/redux/slice/sizeChartSlice";
import AddSizeChart from "./add-size-chart";

// Beyond this the row gets noisy, so the rest collapse into a "+N more" chip.
const VISIBLE_SIZE_CHIPS = 6;

const SizeChart = () => {
  const { isOpen, onClose, onOpen, stageAction, actionProps } = useDisclosure();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();
  const navigate = useNavigate();

  const sizeChartData = useSelector((state) => state?.sizeChart);
  const [searchText, setSearchText] = useState("");
  // Charts read in their configured position by default; unset ones fall to
  // the end, which the API handles.
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("order");
  const [isInitialized, setIsInitialized] = useState(false);

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "size-chart" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );
  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });

  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
      return;
    }
    const permissionValue = {
      createPermission: false,
      updatePermission: false,
      deletePermission: false,
    };
    permissionData?.length &&
      permissionData.forEach((item) => {
        if (item?.name === "size-chart-create") {
          permissionValue.createPermission = true;
        }
        if (item?.name === "size-chart-update") {
          permissionValue.updatePermission = true;
        }
        if (item?.name === "size-chart-delete") {
          permissionValue.deletePermission = true;
        }
      });
    setPermission(permissionValue);
  };

  useEffect(() => {
    getPermission();
    const savedSearchText = localStorage.getItem("size-chart-search") || "";
    setSearchText(savedSearchText);
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      id: "order",
      label: "Order",
      sort: true,
      minWidth: 80,
      render: (row) =>
        row?.order ?? row?.order === 0 ? (
          row.order
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    { id: "name", label: "Name", sort: true },
    {
      id: "sizeIds",
      label: "Sizes",
      render: (row) => {
        const sizes = row?.sizeIds ?? [];
        if (!sizes.length) return "-";
        const shown = sizes.slice(0, VISIBLE_SIZE_CHIPS);
        const rest = sizes.slice(VISIBLE_SIZE_CHIPS);
        return (
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
            {shown.map((size) => (
              <Chip key={size?._id} size="small" label={size?.name} />
            ))}
            {rest.length > 0 && (
              <Tooltip title={rest.map((size) => size?.name).join(", ")}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`+${rest.length} more`}
                />
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      align: "right",
      render: (row) => (
        <>
          {permission.updatePermission && (
            <IconButton onClick={() => stageAction(row)}>
              <EditIcon />
            </IconButton>
          )}
          {permission.deletePermission && (
            <IconButton onClick={() => deleteStageAction(row)}>
              <DeleteIcon color="error" />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  const getData = () => {
    localStorage.setItem("size-chart-search", searchText.trim());
    const value = {
      page,
      perPage: rowsPerPage,
      search: searchText.trim(),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getSizeChartList(value));
  };

  useEffect(() => {
    if (isInitialized) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy, isInitialized]);

  const handleSubmit = async (data) => {
    const payload = {
      name: data.name?.trim(),
      sizeIds: data.sizeIds,
      // Blank means "no position", which the API stores as null.
      order:
        data.order === "" || data.order === null ? null : Number(data.order),
    };
    const response = await dispatch(
      actionProps?._id
        ? updateSizeChart({ id: actionProps._id, data: payload })
        : createSizeChart(payload)
    ).unwrap();

    if (response?.status === 200) {
      enqueueSnackbar(response.data?.responseMessage, { variant: "success" });
      getData();
      onClose();
    } else {
      enqueueSnackbar(response?.data?.responseMessage, { variant: "error" });
    }
  };

  const removeSizeChart = () => {
    dispatch(deleteSizeChart(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, { variant: "success" });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, { variant: "error" });
        }
      });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  return (
    <>
      {sizeChartData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={10}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                              cursor: "pointer",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>

          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant="h2" mb={0}>
                  Size Chart List
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: { xs: 1, md: 0 } }}
                >
                  {/* This page is reached from Sizes rather than the sidebar,
                      so it carries its own way back. */}
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(SIZES)}
                  >
                    Sizes
                  </Button>
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={onOpen}
                      type="submit"
                      variant="contained"
                    >
                      Add Size Chart
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
              <Table
                columns={columns}
                rows={sizeChartData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={sizeChartData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>

          <DialogDefault
            open={isOpen}
            onClose={onClose}
            content={
              <AddSizeChart
                value={actionProps}
                onClose={onClose}
                handleSubmit={handleSubmit}
              />
            }
            title={actionProps?._id ? "Update Size Chart" : "Add Size Chart"}
            fullWidth
            maxWidth="sm"
            actions={<></>}
          />
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete the size chart?"
            onConfirm={removeSizeChart}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default SizeChart;
